
import { Component, Vue } from 'vue-property-decorator';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { firestore } from '@/firebase';
import { ROOT_ACTIONS } from '@/store/actions';
import {
  computeScreencastStats,
  ScreencastStats,
  StatsAssignment,
  StatsContentItem,
} from '@/utils/screencastStats';
import { pickCurrentProgram } from '@/utils/programAccess';

type UserLite = { id: string; firstname: string; lastname: string; type: string };

@Component
export default class ScreencastStatsView extends Vue {
  loading = true;

  items: StatsContentItem[] = [];
  assignments: StatsAssignment[] = [];
  users: Record<string, UserLite> = {};
  participantIds = new Set<string>();
  coachByParticipant: Record<string, string> = {};

  filters = {
    from: '' as string | null,
    to: '' as string | null,
    language: '',
    coachId: null as string | null,
  };

  languageOptions = [
    { text: 'Alle talen', value: '' },
    { text: 'Nederlands', value: 'nl-be' },
    { text: 'Frans', value: 'fr' },
  ];

  headers = [
    { text: 'Screencast', value: 'title' },
    { text: 'Taal', value: 'language' },
    { text: 'Toegewezen door coach', value: 'assignedByCoach', align: 'end' },
    { text: 'Zelf gestart', value: 'selfStarted', align: 'end' },
    { text: 'Bekeken', value: 'viewed', align: 'end' },
    { text: 'Voltooid', value: 'completed', align: 'end' },
    { text: 'Deelnemers', value: 'participants', align: 'end' },
    { text: 'Voltooiingsgraad', value: 'completionRate' },
    { text: 'Uren (voltooid)', value: 'hoursCredited', align: 'end' },
    { text: 'Laatste activiteit', value: 'lastActivity' },
  ];

  coachHeaders = [
    { text: 'Coach', value: 'name' },
    { text: 'Deelnemers', value: 'participants', align: 'end' },
    { text: 'Toegewezen door coach', value: 'assignedByCoach', align: 'end' },
    { text: 'Bekeken', value: 'viewed', align: 'end' },
    { text: 'Voltooid', value: 'completed', align: 'end' },
  ];

  get stats(): ScreencastStats {
    return computeScreencastStats(this.items, this.assignments, this.participantIds, this.coachByParticipant, {
      from: this.filters.from ? new Date(`${this.filters.from}T00:00:00`) : null,
      to: this.filters.to ? new Date(`${this.filters.to}T23:59:59`) : null,
      language: this.filters.language || null,
      coachId: this.filters.coachId,
    });
  }

  get totalCards() {
    const t = this.stats.totals;
    return [
      { key: 'assignments', label: 'Toewijzingen', value: t.assignments },
      { key: 'viewed', label: 'Bekeken', value: t.viewed },
      { key: 'completed', label: 'Voltooid', value: t.completed },
      { key: 'rate', label: 'Voltooiingsgraad', value: `${t.completionRate}%` },
      { key: 'participants', label: 'Unieke deelnemers', value: t.participants },
      { key: 'hours', label: 'Uren (voltooid)', value: t.hoursCredited },
    ];
  }

  get coachRows() {
    return this.stats.coaches.map((coach) => ({ ...coach, name: this.userName(coach.coachId) }));
  }

  get coachOptions() {
    const coachIds = new Set(Object.values(this.coachByParticipant));
    return Array.from(coachIds)
      .map((id) => ({ value: id, text: this.userName(id) }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }

  userName(id: string) {
    const user = this.users[id];
    return user ? `${user.firstname} ${user.lastname}` : id;
  }

  languageLabel(language: string) {
    return this.languageOptions.find((o) => o.value === language)?.text || language || '-';
  }

  formatDate(date: Date | null) {
    return date ? format(date, 'dd/MM/yyyy') : '-';
  }

  async mounted() {
    this.$store.dispatch(ROOT_ACTIONS.SET_BREADCRUMBS, [
      { text: 'Analytics', target: '/admin/analytics' },
      { text: 'Screencasts', target: '/admin/analytics/screencasts' },
    ]);
    try {
      const [itemsSnap, assignmentsSnap, usersSnap, programsSnap] = await Promise.all([
        getDocs(collection(firestore, 'content-items')),
        getDocs(collection(firestore, 'content-assignments')),
        getDocs(collection(firestore, 'users')),
        getDocs(collection(firestore, 'programs')),
      ]);

      this.items = itemsSnap.docs.map((d) => ({ ...(d.data() as StatsContentItem), id: d.id }));
      this.assignments = assignmentsSnap.docs.map((d) => d.data() as StatsAssignment);

      const users: Record<string, UserLite> = {};
      const participantIds = new Set<string>();
      usersSnap.docs.forEach((d) => {
        const data = d.data() as UserLite;
        users[d.id] = { ...data, id: d.id };
        if (data.type === 'PARTICIPANT') participantIds.add(d.id);
      });
      this.users = users;
      this.participantIds = participantIds;

      const programsByParticipant: Record<string, any[]> = {};
      programsSnap.docs.forEach((d) => {
        const data = d.data();
        if (!data.participant || data.isTemplate) return;
        (programsByParticipant[data.participant] = programsByParticipant[data.participant] || []).push(data);
      });
      const coachByParticipant: Record<string, string> = {};
      Object.entries(programsByParticipant).forEach(([participantId, programs]) => {
        const current = pickCurrentProgram(programs);
        if (current?.coach) coachByParticipant[participantId] = current.coach;
      });
      this.coachByParticipant = coachByParticipant;
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, { color: 'error', text: error });
    } finally {
      this.loading = false;
    }
  }

  exportXlsx() {
    const wb = XLSX.utils.book_new();
    const perScreencast = this.stats.rows.map((row) => ({
      Screencast: row.title,
      Taal: this.languageLabel(row.language),
      Gearchiveerd: row.archived ? 'JA' : 'NEE',
      'Toegewezen door coach': row.assignedByCoach,
      'Zelf gestart': row.selfStarted,
      Bekeken: row.viewed,
      Voltooid: row.completed,
      Deelnemers: row.participants,
      'Voltooiingsgraad (%)': row.completionRate,
      'Uren (voltooid)': row.hoursCredited,
      'Laatste activiteit': this.formatDate(row.lastActivity),
    }));
    const perCoach = this.coachRows.map((row) => ({
      Coach: row.name,
      Deelnemers: row.participants,
      'Toegewezen door coach': row.assignedByCoach,
      Bekeken: row.viewed,
      Voltooid: row.completed,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(perScreencast), 'Per screencast');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(perCoach), 'Per coach');
    XLSX.writeFile(wb, `screencast-statistieken-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  }
}
